import { useState, useCallback, useEffect, useMemo } from "react";
import { useAuthenticatedApi } from "./useAuthenticatedApi";
import {
  GuestDirectoryItem,
  GuestDetailedProfile,
  GuestDirectoryResponse,
  createGuestProfilesApi,
} from "../api/GuestProfilesApi";
import { sanitizeString, validatePreference } from "../utils/inputSanitization";

// Custom error class for better error handling
class GuestProfilesError extends Error {
  constructor(message: string, public code?: string) {
    super(message);
    this.name = "GuestProfilesError";
  }
}

export interface UseGuestProfilesReturn {
  // Directory data
  guests: GuestDirectoryItem[];
  isLoading: boolean;
  error: Error | null;

  // Pagination
  pagination: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
  };

  // Filters
  activeFilter: "Current" | "Historic" | "Future" | "All";
  searchQuery: string;

  // Selected guest details
  selectedGuest: GuestDetailedProfile | null;
  isLoadingDetails: boolean;
  detailsError: Error | null;

  // Charge details
  chargeDetails: any;
  isLoadingCharges: boolean;
  chargesError: Error | null;

  // Preference updates
  isUpdatingPreferences: boolean;
  preferencesError: Error | null;

  // Actions
  actions: {
    fetchGuests: () => Promise<void>;
    fetchGuestDetails: (guestId: string) => Promise<void>;
    fetchChargeDetails: (requestId: string) => Promise<void>;
    setActiveFilter: (
      filter: "Current" | "Historic" | "Future" | "All"
    ) => void;
    setSearchQuery: (query: string) => void;
    setPage: (page: number) => void;
    setLimit: (limit: number) => void;
    clearSelectedGuest: () => void;
    addGuestPreference: (guestId: string, preference: string) => Promise<void>;
    removeGuestPreference: (
      guestId: string,
      preferenceId: string
    ) => Promise<void>;
    refetchAll: () => Promise<void>;
  };
}

export const useGuestProfiles = (): UseGuestProfilesReturn => {
  const { callApi } = useAuthenticatedApi();
  const api = useMemo(() => createGuestProfilesApi(callApi), [callApi]);

  // Directory state
  const [guests, setGuests] = useState<GuestDirectoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    totalCount: 0,
    totalPages: 0,
  });

  // Filter state
  const [activeFilter, setActiveFilterState] = useState<
    "Current" | "Historic" | "Future" | "All"
  >("All");
  const [searchQuery, setSearchQueryState] = useState("");

  // Selected guest details
  const [selectedGuest, setSelectedGuest] =
    useState<GuestDetailedProfile | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState<Error | null>(null);

  // Charge details state
  const [chargeDetails, setChargeDetails] = useState<any>(null);
  const [isLoadingCharges, setIsLoadingCharges] = useState(false);
  const [chargesError, setChargesError] = useState<Error | null>(null);

  // Preference updates state
  const [isUpdatingPreferences, setIsUpdatingPreferences] = useState(false);
  const [preferencesError, setPreferencesError] = useState<Error | null>(null);

  // Helper function to handle errors consistently
  const handleError = useCallback((error: unknown, operation: string) => {
    console.error(`Error ${operation}:`, error);
    const errorMessage =
      error instanceof Error ? error.message : `Failed to ${operation}`;
    return new GuestProfilesError(errorMessage);
  }, []);

  // Fetch guests directory - removed from useCallback to avoid infinite loops
  const fetchGuests = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response: GuestDirectoryResponse = await api.getGuestDirectory({
        status: activeFilter,
        search: searchQuery || undefined,
        page: pagination.page,
        limit: pagination.limit,
      });

      setGuests(response.data || []);
      setPagination((prev) => ({
        ...prev,
        totalCount: response.meta.totalCount,
        totalPages: response.meta.totalPages,
      }));
      setError(null);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to fetch guests";
      const guestError = new GuestProfilesError(errorMessage);
      setError(guestError);
      setGuests([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch charge details for a specific request
  const fetchChargeDetails = useCallback(
    async (requestId: string) => {
      if (!requestId) return;

      setIsLoadingCharges(true);
      setChargesError(null);

      try {
        const details = await api.getDiningRequestDetails(requestId);
        setChargeDetails(details);
        setChargesError(null);
      } catch (error) {
        const chargesErr = handleError(error, "fetch charge details");
        setChargesError(chargesErr);
        setChargeDetails(null);
      } finally {
        setIsLoadingCharges(false);
      }
    },
    [api, handleError]
  );

  // Fetch guest details
  const fetchGuestDetails = useCallback(
    async (guestId: string) => {
      if (!guestId) return;

      setIsLoadingDetails(true);
      setDetailsError(null);

      try {
        const guestDetails = await api.getGuestDetails(guestId);
        setSelectedGuest(guestDetails);
        setDetailsError(null);
      } catch (error) {
        const detailsErr = handleError(error, "fetch guest details");
        setDetailsError(detailsErr);
        setSelectedGuest(null);
      } finally {
        setIsLoadingDetails(false);
      }
    },
    [api, handleError]
  );

  // Set active filter and reset pagination
  const setActiveFilter = useCallback(
    (filter: "Current" | "Historic" | "Future" | "All") => {
      // Batch the state updates to avoid race conditions
      setActiveFilterState(filter);
      setPagination((prev) => ({ ...prev, page: 1 }));
    },
    []
  );

  // Set search query and reset pagination
  const setSearchQuery = useCallback((query: string) => {
    setSearchQueryState(query);
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  // Set page
  const setPage = useCallback((page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  }, []);

  // Set limit and reset to page 1
  const setLimit = useCallback((limit: number) => {
    setPagination((prev) => ({ ...prev, limit, page: 1 }));
  }, []);

  // Clear selected guest
  const clearSelectedGuest = useCallback(() => {
    setSelectedGuest(null);
    setDetailsError(null);
  }, []);

  // Add guest preference
  const addGuestPreference = useCallback(
    async (guestId: string, preference: string) => {
      if (!guestId || !preference.trim()) return;

      // Sanitize and validate input
      const sanitizedPreference = sanitizeString(preference.trim(), 100);
      if (!validatePreference(sanitizedPreference)) {
        throw new GuestProfilesError("Invalid preference format");
      }

      setIsUpdatingPreferences(true);
      setPreferencesError(null);

      try {
        // Get current preferences
        const currentPreferences = selectedGuest?.preferences || [];

        // Check if preference already exists
        const existingPref = currentPreferences.find(
          (p) => p.type.toLowerCase() === sanitizedPreference.toLowerCase()
        );

        if (existingPref) {
          throw new GuestProfilesError("This preference already exists");
        }

        // Add new preference
        const updatedPreferences = [
          ...currentPreferences.map((p) => ({ type: p.type, value: p.value })),
          { type: sanitizedPreference, value: sanitizedPreference },
        ];

        const response = await api.updateGuestPreferences(
          guestId,
          updatedPreferences
        );

        // Update selected guest if it's the same one
        if (selectedGuest && selectedGuest.guestId === guestId) {
          setSelectedGuest((prev) =>
            prev
              ? {
                  ...prev,
                  preferences: response.preferences,
                }
              : null
          );
        }

        setPreferencesError(null);
      } catch (error) {
        const prefError = handleError(error, "add guest preference");
        setPreferencesError(prefError);
        throw prefError;
      } finally {
        setIsUpdatingPreferences(false);
      }
    },
    [api, selectedGuest, handleError]
  );

  // Remove guest preference
  const removeGuestPreference = useCallback(
    async (guestId: string, preferenceId: string) => {
      if (!guestId || !preferenceId) return;

      setIsUpdatingPreferences(true);
      setPreferencesError(null);

      try {
        // Get current preferences excluding the one to remove
        const currentPreferences = selectedGuest?.preferences || [];
        const updatedPreferences = currentPreferences
          .filter((p) => p.preferenceId !== preferenceId)
          .map((p) => ({ type: p.type, value: p.value }));

        const response = await api.updateGuestPreferences(
          guestId,
          updatedPreferences
        );

        // Update selected guest if it's the same one
        if (selectedGuest && selectedGuest.guestId === guestId) {
          setSelectedGuest((prev) =>
            prev
              ? {
                  ...prev,
                  preferences: response.preferences,
                }
              : null
          );
        }

        setPreferencesError(null);
      } catch (error) {
        const prefError = handleError(error, "remove guest preference");
        setPreferencesError(prefError);
        throw prefError;
      } finally {
        setIsUpdatingPreferences(false);
      }
    },
    [api, selectedGuest, handleError]
  );

  // Refetch all data
  const refetchAll = useCallback(async () => {
    await Promise.allSettled([
      fetchGuests(),
      selectedGuest
        ? fetchGuestDetails(selectedGuest.guestId)
        : Promise.resolve(),
    ]);
  }, [fetchGuestDetails, selectedGuest]);

  // Fetch guests when dependencies change
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response: GuestDirectoryResponse = await api.getGuestDirectory({
          status: activeFilter,
          search: searchQuery || undefined,
          page: pagination.page,
          limit: pagination.limit,
        });

        // Only update guests if we have a valid response
        if (response && response.data) {
          setGuests(response.data);
          setPagination((prev) => ({
            ...prev,
            totalCount: response.meta?.totalCount || 0,
            totalPages: response.meta?.totalPages || 0,
          }));
        } else {
          setGuests([]);
        }
        setError(null);
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to fetch guests";
        const guestError = new GuestProfilesError(errorMessage);
        setError(guestError);
        setGuests([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [api, activeFilter, searchQuery, pagination.page, pagination.limit]);

  return {
    // Directory data
    guests,
    isLoading,
    error,

    // Pagination
    pagination,

    // Filters
    activeFilter,
    searchQuery,

    // Selected guest details
    selectedGuest,
    isLoadingDetails,
    detailsError,

    // Charge details
    chargeDetails,
    isLoadingCharges,
    chargesError,

    // Preference updates
    isUpdatingPreferences,
    preferencesError,

    // Actions
    actions: {
      fetchGuests,
      fetchGuestDetails,
      fetchChargeDetails,
      setActiveFilter,
      setSearchQuery,
      setPage,
      setLimit,
      clearSelectedGuest,
      addGuestPreference,
      removeGuestPreference,
      refetchAll,
    },
  };
};
